<template>
  <transition
    name="sg-notification-fade"
    @before-leave="onClose"
    @after-leave="$emit('destroy')"
  >
    <div
      v-show="visible"
      :id="id"
      :class="[
        ns.b(),
        { [ns.m(type)]: type && !icon },
        ns.is('description', isDescription),
        customClass,
        horizontalClass
      ]"
      :style="positionStyle"
      role="alert"
      @mouseenter="clearTimer"
      @mouseleave="startTimer"
      @click="onClick"
    >
      <sg-badge
        v-if="repeatNum > 1"
        :value="repeatNum"
        :type="badgeType"
        :class="ns.e('badge')"
      >
      </sg-badge>
      <sg-icon v-if="iconComponent" :class="ns.e('icon')">
        <component :is="iconComponent" />
      </sg-icon>
      <div :class="ns.e('group')">
        <div :class="ns.e('title')">{{ title }}</div>
        <div v-if="isDescription" :class="ns.e('content')">
          <slot>
            {{ description }}
          </slot>
        </div>
        <sg-icon
          v-if="closable"
          :class="ns.e('closebtn')"
          @click.stop="close"
        >
          <Close />
        </sg-icon>
      </div>
    </div>
  </transition>
</template>
<script lang="ts">
import { defineComponent, computed, ref, onMounted, watch } from 'vue'
import { useEventListener, useTimeoutFn } from '@vueuse/core'
import { useGlobalComponentSettings } from '@sgui-plus/hooks'
import { EVENT_CODE } from '@sgui-plus/utils/aria'
import { SgIcon } from '@sgui-plus/components/icon'
import SgBadge from '@sgui-plus/components/badge'
import { TypeComponents, TypeComponentsMap } from '@sgui-plus/utils/icon'
import { notificationProps, notificationEmits } from './notification'
import type { BadgeProps } from '@sgui-plus/components/badge'

import type { CSSProperties } from 'vue'

export default defineComponent({
  name: 'SgNotification',

  components: {
    SgIcon,
    SgBadge,
    ...TypeComponents,
  },

  props: notificationProps,
  emits: notificationEmits,

  setup(props, { slots }) {
    const { ns, zIndex } = useGlobalComponentSettings('notification')
    const { nextZIndex, currentZIndex } = zIndex

    const visible = ref(false)
    let timer: (() => void) | undefined = undefined

    const badgeType = ref<BadgeProps['type']>(
      props.type ? (props.type === 'error' ? 'danger' : props.type) : 'primary'
    )

    const iconComponent = computed(() => {
      return TypeComponentsMap[props.type] || props.icon || ''
    })

    const isDescription = computed(() => !!props.description || !!slots.default)

    const horizontalClass = computed(() =>
      props.position.endsWith('right') ? 'right' : 'left'
    )

    const verticalProperty = computed(() =>
      props.position.startsWith('top') ? 'top' : 'bottom'
    )

    const positionStyle = computed<CSSProperties>(() => {
      return {
        [verticalProperty.value]: `${props.offset}px`,
        zIndex: currentZIndex.value,
      }
    })

    function startTimer() {
      if (props.duration > 0) {
        ;({ stop: timer } = useTimeoutFn(() => {
          if (visible.value) close()
        }, props.duration))
      }
    }

    function clearTimer() {
      timer?.()
    }

    function close() {
      visible.value = false
    }

    function onKeydown({ code }: KeyboardEvent) {
      if (code === EVENT_CODE.delete || code === EVENT_CODE.backspace) {
        clearTimer() // press delete/backspace clear timer
      } else if (code === EVENT_CODE.esc) {
        // press esc to close the notification
        if (visible.value) {
          close()
        }
      } else {
        startTimer() // resume timer
      }
    }

    // lifecycle
    onMounted(() => {
      startTimer()
      nextZIndex()
      visible.value = true
    })

    watch(
      () => props.repeatNum,
      () => {
        clearTimer()
        startTimer()
      }
    )

    useEventListener(document, 'keydown', onKeydown)

    return {
      ns,

      horizontalClass,
      iconComponent,
      isDescription,
      positionStyle,
      visible,
      badgeType,

      close,
      clearTimer,
      startTimer,
    }
  },
})
</script>
